import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import { ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { GroupedBillingView } from "@cx/contracts";
import { formatBRL, formatDateBR, formatDocument } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, isInFlight } from "../invoices/labels";
import { TITLE_STATUS_LABEL } from "../finance/labels";
import { groupedOutputs, groupedState, reverseExplanation } from "./labels";

const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/** Faturamento agrupado: pedidos, parcelas, NF-e e "Desfazer" (estorno, com motivo). */
export function GroupedBillingViewPage(): ReactElement {
  const { id } = useParams({ from: "/financas/faturamento-agrupado/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const view = useQuery({
    queryKey: ["grouped-billing", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<GroupedBillingView>(`/grouped-billing/${id}`),
    // NF-e na fila ou em envio: acompanha até o resultado da SEFAZ (libera ou trava o "Desfazer").
    refetchInterval: (query) => {
      const inv = query.state.data?.invoice;
      return inv && isInFlight(inv.status) ? 2500 : false;
    },
  });
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState("");
  const reverse = useMutation({
    mutationFn: () =>
      api<GroupedBillingView>(`/grouped-billing/${id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason: reason.trim() || null }),
      }),
    onSuccess: (v) => {
      toast("success", `Faturamento agrupado nº ${v.number} desfeito`);
      qc.invalidateQueries({ queryKey: ["grouped-billing", companyId] });
      qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
    },
    onError: (e: Error) => {
      toast("danger", errorMessage(e));
      qc.invalidateQueries({ queryKey: ["grouped-billing", companyId] });
    },
  });
  const g = view.data;
  const canReverse =
    !!g &&
    !g.reversedAt &&
    can(perms, "grouped_billing", "update") &&
    (!g.postReceivables || can(perms, "receivables", "update"));
  const st = g && groupedState(g);

  return (
    <>
      <ContentHeader
        title={g ? `Faturamento agrupado nº ${g.number}` : "Faturamento agrupado"}
        breadcrumb={["Finanças", "Faturamento agrupado", g ? String(g.number) : ""]}
        actions={
          g && (
            <>
              <Link to="/financas/faturamento-agrupado" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {canReverse && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={!!g.reverseBlocker || reverse.isPending}
                  title={g.reverseBlocker ?? undefined}
                  onClick={() => {
                    setReason("");
                    setReversing(true);
                  }}
                >
                  Desfazer faturamento
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={view.isLoading} error={view.error} />
      {g && st && (
        <div className="row">
          <div className="col-xl-9">
            {g.reversedAt && (
              <div className="alert alert-secondary">
                Desfeito em {dateTimeBR(g.reversedAt)}
                {g.reversalReason && ` — ${g.reversalReason}`}. Os pedidos voltaram a ficar livres para
                faturar.
              </div>
            )}
            {!g.reversedAt && g.reverseBlocker && canReverse && (
              <div className="alert alert-info py-2" data-testid="gb-blocker">
                {g.reverseBlocker}
              </div>
            )}
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span>Situação:</span>
                <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                <span className="text-muted small">{groupedOutputs(g)}</span>
              </div>
              <dl className="row mb-0">
                <Info label="Cliente">
                  {g.contact.name}
                  {g.contact.document && (
                    <span className="text-muted ms-2">{formatDocument(g.contact.document)}</span>
                  )}
                </Info>
                <Info label="Data">{formatDateBR(g.date)}</Info>
                <Info label="Total">{formatBRL(g.totalCents)}</Info>
                <Info label="Condição de pagamento">{g.paymentTerm?.name ?? "À vista"}</Info>
                <Info label="Forma de pagamento">{g.paymentMethod?.name}</Info>
                <Info label="Vendedor">{g.seller?.name}</Info>
                {g.issueInvoice && <Info label="Natureza de operação">{g.taxOperation?.name}</Info>}
                {g.invoice && (
                  <Info label="Nota fiscal">
                    {can(perms, "invoices", "read") ? (
                      <Link to="/vendas/notas-fiscais/$id" params={{ id: g.invoice.id }}>
                        {g.invoice.number === null ? "NF-e (rascunho)" : `NF-e nº ${g.invoice.number}`}
                      </Link>
                    ) : g.invoice.number === null ? (
                      "NF-e (rascunho)"
                    ) : (
                      `NF-e nº ${g.invoice.number}`
                    )}{" "}
                    <StatusBadge tone={INVOICE_STATUS_TONE[g.invoice.status]}>
                      {INVOICE_STATUS_LABEL[g.invoice.status]}
                    </StatusBadge>
                  </Info>
                )}
                <Info label="Observações">{g.notes}</Info>
              </dl>
            </div>
            <div className="box card mb-3">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Pedidos do faturamento">
                  <thead>
                    <tr>
                      <th scope="col">Pedido</th>
                      <th scope="col">Data</th>
                      <th scope="col">Contas próprias</th>
                      <th scope="col" className="text-end">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.orders.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <Link to="/vendas/pedidos/$id" params={{ id: o.id }}>
                            Pedido nº {o.number}
                          </Link>
                        </td>
                        <td>{o.date ? formatDateBR(o.date) : "—"}</td>
                        <td>
                          {!g.postReceivables
                            ? "Mantidas no pedido"
                            : o.hadReceivables
                              ? "Passaram ao faturamento"
                              : "Não havia"}
                        </td>
                        <td className="text-end">{formatBRL(o.totalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {g.postReceivables && (
              <div className="box card mb-3">
                <h2 className="h6 p-3 pb-0">Parcelas</h2>
                <div className="table-responsive">
                  <table className="table align-middle mb-0" aria-label="Parcelas do faturamento">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Vencimento</th>
                        <th scope="col" className="text-end">
                          Valor
                        </th>
                        <th scope="col" className="text-end">
                          Pago
                        </th>
                        <th scope="col">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.installments.map((p) => (
                        <tr key={p.titleId}>
                          <td>
                            {can(perms, "receivables", "read") ? (
                              <Link to="/financas/contas-a-receber/$id" params={{ id: p.titleId }}>
                                {p.number}
                              </Link>
                            ) : (
                              p.number
                            )}
                          </td>
                          <td>{formatDateBR(p.dueDate)}</td>
                          <td className="text-end">{formatBRL(p.amountCents)}</td>
                          <td className="text-end">{formatBRL(p.paidCents)}</td>
                          <td>{TITLE_STATUS_LABEL[p.status]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        open={reversing}
        title={g ? `Desfazer faturamento agrupado nº ${g.number}` : "Desfazer faturamento"}
        onClose={() => setReversing(false)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setReversing(false)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={reverse.isPending}
              onClick={() => {
                reverse.mutate();
                setReversing(false);
              }}
            >
              Desfazer
            </button>
          </>
        }
      >
        {g && <p>{reverseExplanation(g)}</p>}
        <Field label="Motivo" className="col-12">
          <input value={reason} maxLength={500} data-autofocus onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
      <dd className="col-sm-8">{children || "—"}</dd>
    </>
  );
}
